import { useEffect, useRef, useState, useMemo } from "react";
import PropTypes from "prop-types";
import Dropzone from "dropzone";
// import "dropzone/dist/dropzone.css"; // Kendi stilimizi kullanıyoruz

import MDBox from "components/MDBox";
import MDDropzoneRoot from "components/MDDropzone/MDDropzoneRoot";
import { useMaterialUIController } from "context";
import CrudService from "services/cruds-service";

import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";

function MDDropzone({ options, onChange, value }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const dropzoneRef = useRef(null);
  const dzInstanceRef = useRef(null);

  const [isDragging, setIsDragging] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "success", msg: "" });

  // Bilgi metni (opsiyonel tooltip için)
  const helperText = useMemo(() => {
    const types = options?.acceptedFiles ? options.acceptedFiles.replaceAll(",", ", ") : "Her tür";
    const max = options?.maxFilesize ? `${options.maxFilesize} MB` : "Sınırsız";
    return `İzin verilen türler: ${types} • Maks. boyut: ${max}`;
  }, [options]);

  useEffect(() => {
    Dropzone.autoDiscover = false;

    const dz = new Dropzone(dropzoneRef.current, {
      // Dropzone form olmayan elemanda url ister; otomatik yükleme kapalı olduğu için hiç kullanılmaz
      url: "#",
      ...options,
      autoProcessQueue: false,
      clickable: true,
      // Dropzone’un preview üretmesini tamamen etkisizleştiriyoruz
      createImageThumbnails: false,
      previewTemplate: "<div></div>",   // boş template => içeride preview oluşmaz
      // Tek dosya kuralı
      maxFiles: options?.maxFiles ?? 1,
      init: function () {
        this.on("addedfile", async (file) => {
          // Tek dosya
          if (this.files.length > 1) {
            this.removeFile(this.files[0]);
          }

          const formData = new FormData();
          formData.append("file", file);

          try {
            const res = await CrudService.checkCustomerForm(formData);
            if (res?.success) {
              if (onChange) onChange([file]);
              setSnack({ open: true, severity: "success", msg: "The file has been verified and added." });
              setIsDisabled(true);
              this.disable();
            } else {
              this.removeFile(file);
              setSnack({
                open: true,
                severity: "error",
                msg: res?.data?.message || "The file was rejected."
              });
            }
          } catch (err) {
            this.removeFile(file);
            setSnack({
              open: true,
              severity: "error",
              msg: "An error occurred during file verification."
            });
          }
        });

        this.on("maxfilesexceeded", function (file) {
          this.removeAllFiles();
          this.addFile(file);
        });

        this.on("dragenter", () => setIsDragging(true));
        this.on("dragleave", () => setIsDragging(false));
        this.on("drop", () => setIsDragging(false));
      },
    });

    dzInstanceRef.current = dz;
    return () => {
      dz.destroy();
      dzInstanceRef.current = null;
    };
  }, [options, onChange]);

  const borderColor =
      isDisabled
          ? (theme) => theme.palette.action.disabled
          : isDragging
              ? (theme) => theme.palette.primary.main
              : (theme) => (darkMode ? theme.palette.grey[700] : theme.palette.grey[300]);

  return (
      <>
        {/* form değil div: bileşen başka formların içinde kullanılıyor (iç içe <form> HTML hatası) */}
        <MDDropzoneRoot
            component="div"
            ref={dropzoneRef}
            className="form-control"
            ownerState={{ darkMode }}
            sx={{
              // 🔒 Boyut sabit — içerik ne olursa olsun değişmez
              width: "100%",
              minHeight: 100,
              height: 100,
              maxHeight: 100,

              p: 3,
              border: "2px dashed",
              borderColor,
              borderRadius: 2,
              bgcolor: (t) => (darkMode ? t.palette.background.default : t.palette.background.paper),
              outline: "none",
              transition: "border-color 120ms ease, box-shadow 120ms ease",
              boxShadow: isDragging ? (t) => `0 0 0 4px ${t.palette.primary.main}22` : "none",
              cursor: isDisabled ? "not-allowed" : "pointer",
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              overflow: "hidden",
              "&:focus-within": {
                boxShadow: (t) => `0 0 0 4px ${t.palette.primary.main}22`,
                borderColor: (t) => t.palette.primary.main,
              },
              // Dropzone'un eklediği tüm preview/parça öğelerini KAPAT
              "& .dz-preview, & .dz-success-mark, & .dz-error-mark, & .dz-progress, & .dz-details, & .dz-filename, & .dz-size":
                  { display: "none !important" },
              "& .dz-message": { display: "flex !important" },
            }}
            aria-disabled={isDisabled}
            aria-label="Dosya yükleme alanı"
            title={helperText}
        >
          {/* 👇 İç kısım HER ZAMAN aynı */}
          <MDBox display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap={1}>
            <CloudUploadIcon fontSize="large" />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Upload
            </Typography>
          </MDBox>

          {/* Fallback input (erişilebilirlik) */}
          <MDBox className="fallback" bgColor="transparent" sx={{ display: "none" }}>
            <MDBox component="input" name="file" type="file" multiple={false} />
          </MDBox>
        </MDDropzoneRoot>

        <Snackbar
            open={snack.open}
            autoHideDuration={3000}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
              onClose={() => setSnack((s) => ({ ...s, open: false }))}
              severity={snack.severity}
              variant="filled"
              iconMapping={{ error: <ErrorOutlineIcon fontSize="inherit" /> }}
              sx={{ width: "100%" }}
          >
            {snack.msg}
          </Alert>
        </Snackbar>
      </>
  );
}

MDDropzone.propTypes = {
  options: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  value: PropTypes.string,
};

export default MDDropzone;
